import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import logo from "../../assets/imgs/MubeginningLogo.png";
import siteConfig from "../../config/siteConfig";
import navBar from "../../assets/imgs/ui/NavBar/nav.png";
import navHome from "../../assets/imgs/ui/NavBar/NavHome.png";
import navDownload from "../../assets/imgs/ui/NavBar/NavDownload.png";
import navRanking from "../../assets/imgs/ui/NavBar/NavRanking.png";
import navGuilds from "../../assets/imgs/ui/NavBar/NavGuilds.png";
import navEvents from "../../assets/imgs/ui/NavBar/NavEvents.png";
import navGuides from "../../assets/imgs/ui/NavBar/NavGuides.png";
import navRegister from "../../assets/imgs/ui/NavBar/NavRegister.png";
import navSupport from "../../assets/imgs/ui/NavBar/navSupport.png";
import NavProfile from "../../assets/imgs/ui/NavBar/profile.png";
import "./SiteNav.scss";

const leftLinks = [
  { label: "HOME", to: "/", img: navHome },
  { label: "DOWNLOADS", to: "/download", img: navDownload },
  { label: "RANKINGS", to: "/ranking", img: navRanking },
  { label: "GUILDS", to: "/guilds", img: navGuilds },
];

const SiteNav = () => {
  const location = useLocation();
  const user = useSelector((state) => state.reducerUser.userLogin);
  const isAuthed = user?.status === "fulfilled";
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  const registerLink = {
    label: isAuthed ? "PROFILE" : "REGISTER",
    to: isAuthed ? "/profile" : "/register",
    img: isAuthed ? NavProfile : navRegister,
  };

  const rightLinks = [
    { label: "EVENTS", to: "/events", img: navEvents },
    { label: "GUIDES", to: "/guides", img: navGuides },
    registerLink,
    {
      label: "SUPPORT",
      href: siteConfig.social.discordInvite,
      img: navSupport,
    },
  ];

  /* Mobile menu grid order: 2×4 (and 4×2 on tablet) */
  const mobileGridLinks = [
    leftLinks[0], // HOME
    leftLinks[2], // RANKINGS
    leftLinks[1], // DOWNLOADS
    leftLinks[3], // GUILDS
    rightLinks[0], // EVENTS
    rightLinks[1], // GUIDES
    registerLink, // REGISTER / PROFILE
    rightLinks[3], // SUPPORT
  ];

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    closeMenu();
  }, [location.pathname, location.hash, closeMenu]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    const onResize = () => {
      if (window.innerWidth > 1024) closeMenu();
    };

    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointer = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
    };
  }, [menuOpen, closeMenu]);

  const isActive = (item) =>
    !item.hash &&
    !item.href &&
    (location.pathname === item.to ||
      (item.to === "/register" && location.pathname === "/profile") ||
      (item.to === "/profile" && location.pathname === "/register"));

  const renderLink = (item, imgClass = "mu-nav__item-img") => {
    const className = `mu-nav__link${isActive(item) ? " is-active" : ""}`;
    const content = (
      <img className={imgClass} src={item.img} alt={item.label} />
    );

    if (item.href) {
      return (
        <a
          key={item.label}
          className={className}
          href={item.href}
          target="_blank"
          rel="noreferrer"
          onClick={closeMenu}
        >
          {content}
        </a>
      );
    }

    return (
      <Link
        key={item.label}
        className={className}
        to={item.to}
        onClick={closeMenu}
      >
        {content}
      </Link>
    );
  };

  return (
    <nav
      ref={navRef}
      className={`mu-nav${menuOpen ? " is-menu-open" : ""}`}
      aria-label="Main"
    >
      {/* ===== DESKTOP (unchanged layout) ===== */}
      <div className="mu-nav__desktop">
        <img className="mu-nav__bar" src={navBar} alt="" aria-hidden="true" />
        <div className="mu-nav__inner">
          <div className="mu-nav__group mu-nav__group--left">
            {leftLinks.map((item) => renderLink(item))}
          </div>

          <Link to="/" className="mu-nav__logo">
            <img src={logo} alt="Mu Beginning Season 6" />
          </Link>

          <div className="mu-nav__group mu-nav__group--right">
            {rightLinks.map((item) => renderLink(item))}
          </div>
        </div>
      </div>

      {/* ===== MOBILE / TABLET ===== */}
      <div className="mu-nav__mobile">
        <div className="mu-nav__mobile-bar">
          <button
            type="button"
            className={`mu-nav__menu-btn${menuOpen ? " is-open" : ""}`}
            aria-expanded={menuOpen}
            aria-controls="mu-mobile-menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="mu-nav__menu-btn-lines" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            <span className="mu-nav__menu-btn-label">
              {menuOpen ? "CLOSE" : "MENU"}
            </span>
          </button>

          <Link to="/" className="mu-nav__mobile-logo" onClick={closeMenu}>
            <img src={logo} alt="Mu Beginning Season 6" />
          </Link>

          <Link
            to={registerLink.to}
            className={`mu-nav__mobile-register${
              isActive(registerLink) ? " is-active" : ""
            }`}
            onClick={closeMenu}
          >
            <img src={registerLink.img} alt={registerLink.label} />
          </Link>
        </div>

        <div
          id="mu-mobile-menu"
          className={`mu-nav__mobile-panel${menuOpen ? " is-open" : ""}`}
          hidden={!menuOpen}
        >
          <div className="mu-nav__mobile-grid">
            {mobileGridLinks.map((item) =>
              renderLink(item, "mu-nav__mobile-item-img"),
            )}
          </div>
        </div>

        {menuOpen ? (
          <button
            type="button"
            className="mu-nav__mobile-backdrop"
            aria-label="Close menu"
            onClick={closeMenu}
          />
        ) : null}
      </div>
    </nav>
  );
};

export default SiteNav;
